import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { OutputState } from '../../shared/api/types';
import { Alert, Button, Card } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { CountdownView, SlideView, THEME_FONT_FAMILY, useCountdown } from './SlideView';
import { usePolling } from './usePolling';
import { usePresentationSocket } from './usePresentationSocket';
import { Crossfade } from './Crossfade';
import { isLapsedError, LapsedScreen } from './Lapsed';
import { BigTimer, screenId } from './OutputPage';

/**
 * Salida para OBS/vMix (fuente de navegador, sin sesión). «tercio»: texto abajo sobre fondo
 * transparente para montarlo sobre la cámara; «completo»: la diapositiva a pantalla completa.
 * Llena todo el tamaño que le dé OBS y cambia con fundido cruzado.
 */
export function ObsOutputPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const style = params.get('estilo') === 'completo' ? 'completo' : 'tercio';
  const refresh = useRef(() => {});
  const instant = usePresentationSocket({ token }, () => refresh.current());
  const state = usePolling((signal) => api.get<OutputState>(`/presentation/public/obs/${token}`, signal), instant ? 5000 : 1000, [token]);
  refresh.current = state.refresh;
  useEffect(() => {
    document.documentElement.classList.add('obs-root');
    return () => document.documentElement.classList.remove('obs-root');
  }, []);
  const s = state.data;
  const fileUrl = (rid: string) => `/api/presentation/public/obs/${token}/files/${rid}`;
  const countdown = useCountdown(s?.timer?.endsAt ?? null, s?.serverTime ?? null);
  if (isLapsedError(state.error)) return <LapsedScreen transparent={style === 'tercio'} />;
  const id = s ? `${style}|${screenId(s, Boolean(s.countdown && countdown), style === 'completo')}` : 'cargando';
  return (
    <>
      <Crossfade id={id} className="xfade--fill">
        <ObsView s={s ?? null} style={style} countdown={countdown} fileUrl={fileUrl} />
      </Crossfade>
      {/* El cronómetro que ve la congregación, grande al centro (también en la transmisión). */}
      {s && countdown && countdown !== '0:00' && !s.countdown && !s.blackout ? <BigTimer time={countdown} label={s.timer?.label ?? null} /> : null}
    </>
  );
}

function ObsView({ s, style, countdown, fileUrl }: { s: OutputState | null; style: 'completo' | 'tercio'; countdown: string | null; fileUrl: (rid: string) => string }) {
  if (s && style === 'completo' && s.countdown && countdown && !s.blackout) {
    return (
      <div className="obs obs--completo">
        <CountdownView label={s.timer?.label ?? null} time={countdown} theme={s.theme} logo={s.brand ?? { resourceId: null, name: s.title }} fileUrl={fileUrl} />
      </div>
    );
  }
  if (s && style === 'completo' && s.logo && !s.blackout) {
    return (
      <div className="obs obs--completo">
        <SlideView slide={null} fileUrl={fileUrl} theme={s.theme} logo={s.logo} />
      </div>
    );
  }
  // Abajo sobre la cámara, el logo no se pone: la transmisión queda limpia.
  if (!s || s.blackout || !s.live || s.logo || s.countdown) return <div className={`obs obs--${style}`} />;
  if (style === 'completo' || s.live.kind === 'media') {
    return (
      <div className={`obs obs--completo${s.live.kind === 'media' && style === 'tercio' ? ' obs--media' : ''}`}>
        <SlideView slide={s.live} fileUrl={fileUrl} muted theme={style === 'completo' ? s.theme : null} background={style === 'completo' ? s.liveBackground : null} hideText={s.textHidden} player={{ playback: s.playback, serverTime: s.serverTime, muted: true }} />
      </div>
    );
  }
  // El tercio usa la letra del tema; el recuadro oscuro se queda para que se lea sobre cualquier toma.
  return (
    <div className="obs obs--tercio" style={s.theme ? { fontFamily: THEME_FONT_FAMILY[s.theme.font], textTransform: s.theme.uppercase ? 'uppercase' : undefined } : undefined}>
      {s.live.text ? (
        <div className="obs__third">
          <p className="obs__text">{s.live.text}</p>
          {s.live.caption ? <p className="obs__caption">{s.live.caption}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

/** En la consola: crear o quitar el enlace para OBS/vMix. */
export function ObsLinkCard({ churchId, presentationId }: { churchId: string; presentationId: string }) {
  const url = `/presentation/churches/${churchId}/presentations/${presentationId}/obs`;
  const data = useAsync((signal) => api.get<{ token: string | null }>(url, signal), [url]);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  if (data.status !== 'ready') return null;
  const token = data.data.token;
  const link = (style: string) => `${window.location.origin}/obs/${token}${style === 'completo' ? '?estilo=completo' : ''}`;
  const set = (on: boolean) => api.put(url, { on }).then(() => data.reload()).catch((e) => setError(errorMessage(e)));
  const copy = (style: string) => navigator.clipboard.writeText(link(style)).then(() => setCopied(style)).catch(() => undefined);
  return (
    <Card title="Salida para OBS / vMix (transmisión)">
      {error ? <Alert>{error}</Alert> : null}
      <p className="small muted">En OBS agrega una «Fuente de navegador» con este enlace (1920×1080). No necesita iniciar sesión: compártelo solo con el equipo de transmisión.</p>
      {token ? (
        <>
          <p className="small"><strong>Texto abajo, fondo transparente</strong> (para ponerlo sobre la cámara):</p>
          <div className="code-row"><code className="code">{link('tercio')}</code><Button variant="secondary" onClick={() => void copy('tercio')}>{copied === 'tercio' ? 'Copiado' : 'Copiar'}</Button></div>
          <p className="small"><strong>Pantalla completa</strong>:</p>
          <div className="code-row"><code className="code">{link('completo')}</code><Button variant="secondary" onClick={() => void copy('completo')}>{copied === 'completo' ? 'Copiado' : 'Copiar'}</Button></div>
          <Button variant="ghost" onClick={() => void set(false)}>Desactivar enlace</Button>
        </>
      ) : (
        <Button variant="secondary" onClick={() => void set(true)}>Crear enlace para OBS</Button>
      )}
    </Card>
  );
}
